import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../styles/mobile/FDCSubmissions.css';
import {
  getHeaderStatus,
  getHeaderClass,
  getHeaderIcon,
  getPercentageClass,
  getCountClass,
  getMetricClass
} from '../utils/statusHelpers';
import { StatusEvaluator } from '../context/StatusEvaluator';
import { LastGoodStatusStore } from '../utils/LastGoodStatusStore';

function MobileFDCDataSummary({ 
  data: summaryDataAll,
  fdcDataAll,
  onMissingRoundsClick,
  currentSlide,
  onSlideChange
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, transform: 'translateX(-50%) translateY(-100%)' });
  const [tooltipData, setTooltipData] = useState({ entity: '', network: '', timeframe: '', timeframeData: null, invalidAttestations: [] });
  
  const summaryWrapperRef = useRef(null);

  // Summary configurations - all cards in a horizontal scroll
  const summaryConfigs = [
    { entity: 'UYS', network: 'flare', title: 'UYS Flare' },
    { entity: 'UYS', network: 'songbird', title: 'UYS Songbird' },
    { entity: 'ENV', network: 'flare', title: 'Envision Flare' },
    { entity: 'ENV', network: 'songbird', title: 'Envision Songbird' }
  ];

  const handleScroll = useCallback((event) => {
    const scroller = event.target;
    if (!scroller || !onSlideChange) return;

    let firstSlideElement;
    let effectiveSlideWidth;
    let cardWidthFromElement = 0;
    let calculatedGap = 0;
    const localSummaryConfigsLength = summaryConfigs.length;

    if (scroller.children.length > 0) {
      firstSlideElement = scroller.children[0];
      cardWidthFromElement = firstSlideElement.offsetWidth;
      calculatedGap = 20;
      effectiveSlideWidth = cardWidthFromElement + calculatedGap;
    } else {
      return;
    }

    if (!firstSlideElement || effectiveSlideWidth === undefined || effectiveSlideWidth === 0) {
      return;
    }

    const scrollPosition = scroller.scrollLeft;
    const newSlide = Math.round((scrollPosition + (effectiveSlideWidth * 0.1)) / effectiveSlideWidth);

    if (newSlide !== currentSlide && newSlide >= 0 && newSlide < localSummaryConfigsLength) {
      onSlideChange(newSlide);
    }
  }, [currentSlide, onSlideChange, summaryConfigs.length]);

  useEffect(() => {
    const wrapper = summaryWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('scroll', handleScroll);
      return () => wrapper.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const getFDCHeaderStatus = (entityData) => {
    if (!entityData) return 'red';
    const sixHourPercentage = entityData?.['6hr']?.percentage || 0;
    
    if (sixHourPercentage >= 95) {
      return 'green';
    } else if (sixHourPercentage >= 85) {
      return 'amber';
    } else {
      return 'red';
    }
  };

  const calculateFDCAverage = (entityData) => {
    if (!entityData) return 0;
    
    // Get 6hr attestation data and calculate valid/total percentage (for display only)
    const sixHourData = entityData?.['6hr'];
    if (!sixHourData) return 0;

    const valid = sixHourData?.attestations?.valid || sixHourData?.valid_attestations || 0;
    const total = sixHourData?.attestations?.total || sixHourData?.total_attestations || 0;

    if (total === 0) return 0;

    const percentage = (valid / total) * 100;
    
    const rounded = Math.round(percentage);
    if (Math.abs(percentage - rounded) < 0.01) {
      return rounded;
    } else {
      return Math.round(percentage * 10) / 10;
    }
  };

  const getDefaultFDCData = () => ({
    '15min': {
      attestations: { percentage: 0 },
      signatures: { percentage: 0 },
      reveal_offences: { count: 0 },
      missing_rounds: []
    },
    '2hr': {
      attestations: { percentage: 0 },
      signatures: { percentage: 0 },
      reveal_offences: { count: 0 },
      missing_rounds: []
    },
    '6hr': {
      attestations: { percentage: 0 },
      signatures: { percentage: 0 },
      reveal_offences: { count: 0 },
      missing_rounds: []
    }
  });

  const getInvalidAttestations = (entity, network, timeframe) => {
    if (!fdcDataAll || !fdcDataAll[entity] || !fdcDataAll[entity][network]) {
      return [];
    }

    const entityNetworkData = fdcDataAll[entity][network];
    let attestationArray = [];
    
    if (Array.isArray(entityNetworkData)) {
      attestationArray = entityNetworkData;
    } else if (typeof entityNetworkData === 'object') {
      const votingRoundKeys = Object.keys(entityNetworkData);
      
      attestationArray = [];
      votingRoundKeys.forEach(vrKey => {
        const vrData = entityNetworkData[vrKey];
        if (Array.isArray(vrData)) {
          const attestationsWithVR = vrData.map(att => ({...att, voting_round_id: vrKey}));
          attestationArray = attestationArray.concat(attestationsWithVR);
        } else if (vrData && typeof vrData === 'object') {
          if (vrData.attestations && Array.isArray(vrData.attestations)) {
            const attestationsWithVR = vrData.attestations.map(att => ({...att, voting_round_id: vrKey}));
            attestationArray = attestationArray.concat(attestationsWithVR);
          } else {
            attestationArray.push({...vrData, voting_round_id: vrKey});
          }
        }
      });
    }
    
    const invalidAttestations = attestationArray.filter(attestation => 
      attestation.bitvote_result !== true
    );
    
    return invalidAttestations;
  };

  const handleMouseEnter = (event, entity, network, timeframe, timeframeData) => {
    const valid = timeframeData?.attestations?.valid || timeframeData?.valid_attestations || 0;
    const total = timeframeData?.attestations?.total || timeframeData?.total_attestations || 0;
    
    if (valid < total) {
      const rect = event.currentTarget.getBoundingClientRect();
      const tooltipWidth = 320;
      const scrollX = window.pageXOffset;
      const scrollY = window.pageYOffset;
      
      let x = rect.left + scrollX + rect.width / 2;
      let y = rect.top + scrollY;
      let transform = 'translateX(-50%) translateY(-100%)';
      
      if (x - tooltipWidth / 2 < 10) {
        x = rect.left + scrollX;
        transform = 'translateY(-100%)';
      } else if (x + tooltipWidth / 2 > window.innerWidth - 10) {
        x = rect.right + scrollX;
        transform = 'translateX(-100%) translateY(-100%)';
      }
      
      if (y - 200 < scrollY + 10) {
        y = rect.bottom + scrollY;
        transform = transform.replace('translateY(-100%)', 'translateY(10px)');
      }
      
      const invalidAttestations = getInvalidAttestations(entity, network, timeframe);
      
      setTooltipData({
        entity,
        network,
        timeframe,
        timeframeData,
        invalidAttestations
      });
      
      setTooltipPosition({ x, y, transform });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const getTotalAttestationsClass = (total, timeframe) => {
    const votingRounds = {
      '15min': 10,
      '2hr': 80, 
      '6hr': 240
    };
    
    const expectedRounds = votingRounds[timeframe] || 1;
    const minExpected = expectedRounds * 1; // 2 attestations per voting round
    
    if (total === 0) {
      return 'color-red';
    } else if (total < minExpected) {
      return 'color-amber';
    } else {
      return 'color-green';
    }
  };

  const getFDCAttestationPercentageClass = (percentage) => {
    if (percentage === 100) {
      return 'color-green';
    } else if (percentage >= 99) {
      return 'color-amber';
    } else {
      return 'color-red';
    }
  };

  const getValidAttestationsClass = (valid, total) => {
    if (total === 0) {
      return 'color-red'; // No attestations at all
    } else if (valid === total) {
      return 'color-green'; // All valid
    } else if (valid === total - 1) {
      return 'color-amber'; // One invalid
    } else {
      return 'color-red'; // More than one invalid
    }
  };

  const renderCard = ({ entity, network, title }) => {
    const entitySubmissionSummary = summaryDataAll?.[entity]?.[network] || getDefaultFDCData();
    const latestRoundForDisplay = summaryDataAll?.[entity]?.[network]?.latest_voting_round || 'N/A';
    
    let headerStatus = StatusEvaluator.evaluateFdcSummaryBoxStatus(entitySubmissionSummary);
    if (!headerStatus) {
      const lg = LastGoodStatusStore.get('fdc-submissions', `${entity}|${network}`);
      if (lg) headerStatus = lg;
    } else {
      LastGoodStatusStore.set('fdc-submissions', `${entity}|${network}`, headerStatus);
    }
    const headerClass = getHeaderClass(headerStatus || 'red');
    const headerIconSrc = getHeaderIcon(headerStatus);

    return (
      <div key={`${entity}-${network}`} className="mobile-summary-box">
        <h2 className={headerClass}>
          <div className="summary-box-title-group-mobile">
            <span>{title}</span>
            <span className="ftso-overview-latest-round">
              ({latestRoundForDisplay})
            </span>
          </div>
          <img
            src={headerIconSrc}
            alt="Status Icon"
            className="header-icon"
          />
        </h2>

        <div className="summary-content">
          {/* Attestations Section */}
          <div className="section-header">
            <span className="section-title">ATTESTATIONS</span>
            <span
              className={`section-value ${getFDCAttestationPercentageClass(
                calculateFDCAverage(entitySubmissionSummary)
              )}`}
            >
              {calculateFDCAverage(entitySubmissionSummary)}%
            </span>
          </div>

          <div className="summary-grid">
              <span className="grid-header"></span>
              <span className="grid-header">15min</span>
              <span className="grid-header">2hr</span>
              <span className="grid-header">6hr</span>

              <span className="method-label">total:</span>
              {['15min', '2hr', '6hr'].map((timeframe) => {
                const timeframeData = entitySubmissionSummary?.[timeframe];
                const total = timeframeData?.attestations?.total || timeframeData?.total_attestations || 0;
                
                return (
                  <span
                    key={`total-${timeframe}`}
                    className={`percentage ${getTotalAttestationsClass(total, timeframe)}`}
                  >
                    {total}
                  </span>
                );
              })}

              <span className="method-label">valid:</span>
              {['15min', '2hr', '6hr'].map((timeframe) => {
                const timeframeData = entitySubmissionSummary?.[timeframe];
                const valid = timeframeData?.attestations?.valid || timeframeData?.valid_attestations || 0;
                const total = timeframeData?.attestations?.total || timeframeData?.total_attestations || 0;
                const showHover = valid < total;
                
                return (
                  <span
                    key={`valid-${timeframe}`}
                    className={`percentage ${getValidAttestationsClass(valid, total)}`}
                    style={{cursor: showHover ? 'pointer' : 'default'}}
                    onMouseEnter={showHover ? (e) => handleMouseEnter(e, entity, network, timeframe, timeframeData) : undefined}
                    onMouseLeave={showHover ? handleMouseLeave : undefined}
                  >
                    {valid}
                  </span>
                );
              })}

              <span className="method-label">offences:</span>
              {['15min', '2hr', '6hr'].map((timeframe) => {
                const timeframeData = entitySubmissionSummary?.[timeframe];
                const count = timeframeData?.reveal_offences?.count || 0;
                
                return (
                  <span
                    key={`off-${timeframe}`}
                    className={`percentage ${getCountClass(count)}`}
                  >
                    {count}
                  </span>
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="mobile-summary-wrapper" ref={summaryWrapperRef}>
        {summaryConfigs.map(config => renderCard(config))}
      </div>
      
      {/* Tooltip Modal */}
      {showTooltip && createPortal(
        <div 
          className="fdc-attestation-modal"
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: tooltipPosition.transform,
            zIndex: 9999,
            minWidth: '220px',
            maxWidth: '320px',
            padding: '16px',
            pointerEvents: 'none'
          }}
        >
          <div className="fdc-attestation-modal-header">
            Invalid Attestations ({tooltipData.invalidAttestations?.length || 0}):
          </div>
          
          {(() => {
            const data = tooltipData.timeframeData;
            const total = data?.attestations?.total || data?.total_attestations || 0;
            const valid = data?.attestations?.valid || data?.valid_attestations || 0;
            const invalid = total - valid;
            const offences = data?.reveal_offences?.count || 0;
            
            return (
              <>
                {tooltipData.invalidAttestations && tooltipData.invalidAttestations.length > 0 ? (
                  <>
                    {tooltipData.invalidAttestations.map((attestation, index) => (
                      <div key={index} className="fdc-attestation-modal-item">
                        <div
                          className="fdc-status-circle invalid"
                          style={{ marginRight: '12px', minWidth: '12px', flexShrink: 0 }}
                        />
                        <div className="fdc-attestation-modal-text">
                          <span className="fdc-attestation-modal-type">
                            VR {attestation.voting_round_id || 'N/A'}: 
                          </span>
                          <span className="fdc-attestation-modal-source">
                            {attestation.attestation_type} ({attestation.source_id})
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="fdc-attestation-modal-item">
                    <div
                      className="fdc-status-circle invalid"
                      style={{ marginRight: '12px', minWidth: '12px', flexShrink: 0 }}
                    />
                    <div className="fdc-attestation-modal-text">
                      <span className="fdc-attestation-modal-type">
                        No detailed data available
                      </span>
                      <span className="fdc-attestation-modal-source">
                        {invalid} invalid out of {total} total
                      </span>
                    </div>
                  </div>
                )}
                
                {offences > 0 && (
                  <div className="fdc-attestation-modal-item">
                    <div
                      className="fdc-status-circle invalid"
                      style={{ marginRight: '12px', minWidth: '12px', flexShrink: 0 }}
                    />
                    <div className="fdc-attestation-modal-text">
                      <span className="fdc-attestation-modal-type">
                        Reveal Offences: 
                      </span>
                      <span className="fdc-attestation-modal-source">
                        {offences}
                      </span>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>,
        document.body
      )}
    </>
  );
}

export default MobileFDCDataSummary; 